import React, { FormEvent, useContext } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import './styles.scss'
import { LocaleContext } from '../../contexts/locale'
import { SubmissionStatus } from 'types/submissionStatus'
import { parseHostname } from '@ta-interaktiv/parse-hostname'
import { VerticalSegment } from 'components/verticalSegment'

interface SubmitFormProps {
  emailAddress: string
  firstName: string
  lastName: string
  confirmedTerms: boolean
  isComplete: boolean
  submissionStatus: SubmissionStatus
  firebaseErrorCode?: string
}

interface SubmitFormHandlers {
  setEMailAddress: (address: string) => void
  setFirstName: (firstName: string) => void
  setLastName: (lastName: string) => void
  toggleTerms: () => void
  submit: () => void
}

export function SubmitForm({
  emailAddress = '',
  firstName = '',
  lastName = '',
  confirmedTerms = false,
  isComplete,
  submissionStatus,
  firebaseErrorCode,
  submit,
  setEMailAddress,
  setFirstName,
  setLastName,
  toggleTerms
}: SubmitFormProps & SubmitFormHandlers) {
  if (submissionStatus === 'delivered') return null

  const locale = useContext(LocaleContext)
  const messages = require('./locales/' + locale + '.yml')

  const formSubmission = (event: FormEvent) => {
    event.preventDefault()
    if (isComplete) {
      submit()
    }
  }

  const termsLink = `https://abo.${
    parseHostname().publicationDomain
  }/tamstorefront/terms/register`

  return (
    <VerticalSegment text className={'submitForm'}>
      <div className='contest-submit'>
        <div
          className='ui vertical infographic inverted basic segment'
          dangerouslySetInnerHTML={{ __html: messages.formIntroduction }}
        />
        <form
          onSubmit={formSubmission}
          className={`ui inverted contest-submit ${submissionStatus ===
            'error' &&
            firebaseErrorCode &&
            'error'} form`}
        >
          <div className='two fields'>
            <div className='field'>
              <label>{messages.firstName}</label>
              <input
                type='text'
                value={firstName}
                placeholder='Jon'
                onChange={event => setFirstName(event.target.value)}
              />
            </div>
            <div className='field'>
              <label>{messages.lastName}</label>
              <input
                type='text'
                value={lastName}
                placeholder='Snow'
                onChange={event => setLastName(event.target.value)}
              />
            </div>
          </div>
          <div className='field'>
            <label>{messages.email}</label>
            <input
              type='email'
              name='email'
              onChange={event => setEMailAddress(event.target.value)}
              value={emailAddress}
              placeholder='jon.snow@nightswatch.wall'
            />
            <div className='ui pointing above label grey'>
              {messages.emailRemark}
            </div>
          </div>
          <div className='field'>
            <div className='ui checkbox' onClick={toggleTerms}>
              <input
                type='checkbox'
                onChange={toggleTerms}
                checked={confirmedTerms}
                tabIndex={0}
                className='hidden'
              />
              <label>
                {messages.terms} (
                <a href={termsLink} target='_blank' rel='noopener'>
                  <i className='small external link icon' />
                  {messages.readTerms}
                </a>
                )
              </label>
            </div>
          </div>

          <div className='ui small inverted vertical very fitted secondary basic infographic segment'>
            <div className='content'>
              <div className='ui inverted list'>
                <div className='item'>{messages.noCorrespondence}</div>
                <div className='item'>{messages.rights}</div>
              </div>
            </div>
          </div>

          <div className='ui error message'>
            <div className='content'>
              <div className='header'>{messages.error.header}</div>
              {firebaseErrorCode && (
                <p>{messages.error.reasons[firebaseErrorCode]}</p>
              )}
            </div>
          </div>

          <button
            className={`ui ${submissionStatus === 'submitting' &&
              'loading'} large primary button`}
            type='submit'
            disabled={!isComplete}
          >
            {messages.submit}
          </button>
        </form>
      </div>
    </VerticalSegment>
  )
}
